import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner, PageSkeleton } from '@daycal/ui';
import { api } from '../lib/apiClient.js';
import { FiTrash2, FiCalendar, FiChevronLeft, FiChevronRight, FiPercent, FiTag } from 'react-icons/fi';

const DEFAULT_FORM = {
  code: '',
  discount_type: 'percentage',
  discount_value: '',
  max_uses: '',
  expires_at: '',
};

export default function PromoCodes() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['promos'], queryFn: api.listPromos });
  const [form, setForm] = useState(DEFAULT_FORM);
  const [error, setError] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.createPromo({
        code: form.code,
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
      }),
    onSuccess: () => {
      setForm(DEFAULT_FORM);
      setError('');
      qc.invalidateQueries({ queryKey: ['promos'] });
    },
    onError: (err) => setError(err.message),
  });

  const toggle = useMutation({
    mutationFn: ({ id, isDisabled }) => api.updatePromo(id, isDisabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promos'] }),
  });

  const remove = useMutation({
    mutationFn: (id) => api.deletePromo(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promos'] }),
  });

  if (isLoading) return <PageSkeleton />;
  const promos = data?.promo_codes || [];

  return (
    <div className="animate-dc-rise mx-auto max-w-6xl space-y-6">
      {/* Create Card */}
      <div className="rounded-dc border border-slate-200/80 bg-white p-6 md:p-8 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200/80 pb-5 mb-6">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="font-display text-2xl font-extrabold text-slate-900">My Promo Codes</h1>
              <span className="rounded-full bg-brand-50 border border-brand-200 px-2.5 py-0.5 text-xs font-bold text-brand-700 shadow-xs">
                {promos.length} Available
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Create promotional discount coupons to offer clients when booking your services.
            </p>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Promo Code *</span>
            <div className="relative">
              <input
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="OFFER15"
                className="w-full h-10 rounded-dc-sm border border-slate-300/90 bg-white pl-8 pr-3.5 text-xs font-bold uppercase tracking-wider text-slate-900 outline-none transition-all hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 shadow-2xs"
              />
              <FiTag className="absolute left-2.5 top-3 h-3.5 w-3.5 text-slate-400" />
            </div>
          </label>

          <label className="block relative z-20">
            <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Discount Type *</span>
            <CustomSelect
              options={[
                { value: 'percentage', label: 'Percentage (%)' },
                { value: 'fixed', label: 'Fixed Amount' },
              ]}
              value={form.discount_type}
              onChange={(val) => setForm((f) => ({ ...f, discount_type: val }))}
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Discount Value *</span>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                value={form.discount_value}
                onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
                placeholder="15.00"
                className="w-full h-10 rounded-dc-sm border border-slate-300/90 bg-white pl-8 pr-3.5 text-xs font-medium text-slate-800 outline-none transition-all hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 shadow-2xs"
              />
              <FiPercent className="absolute left-2.5 top-3 h-3.5 w-3.5 text-slate-400" />
            </div>
          </label>

          <label className="block">
            <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Max Uses (Optional)</span>
            <input
              type="number"
              value={form.max_uses}
              onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
              placeholder="50"
              className="w-full h-10 rounded-dc-sm border border-slate-300/90 bg-white px-3.5 text-xs font-medium text-slate-800 outline-none transition-all hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 shadow-2xs"
            />
          </label>
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-2">
          <label className="block relative z-10">
            <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Expiration Date (Optional)</span>
            <CustomDatePicker 
              value={form.expires_at}
              onChange={(val) => setForm((f) => ({ ...f, expires_at: val }))}
            />
          </label>
        </div>

        {error && <p className="mt-4 text-xs font-semibold text-rose-600">{error}</p>}

        <div className="mt-7 flex justify-end border-t border-slate-100 pt-6">
          <button
            onClick={() => create.mutate()}
            disabled={create.isPending || !form.code || !form.discount_value}
            className="flex h-10 items-center justify-center rounded-dc-sm bg-slate-900 px-6 text-xs font-semibold text-white shadow-xs transition-all hover:bg-brand-600 active:scale-95 disabled:opacity-50 cursor-pointer"
          >
            {create.isPending ? <Spinner className="!h-4 !w-4 !border-t-white" /> : 'Create Promo Code'}
          </button>
        </div>
      </div>

      {/* List Card */}
      <div className="rounded-dc border border-slate-200/80 bg-white p-6 md:p-8 shadow-xs min-h-[300px]">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Your Coupons</h2>
            <p className="mt-0.5 text-xs text-slate-500">List of active promotional coupon codes & discount rates.</p>
          </div>
          <span className="rounded-full bg-slate-100 border border-slate-200/60 px-3 py-1 text-xs font-bold text-slate-600">
            {promos.length} {promos.length === 1 ? 'Coupon' : 'Coupons'}
          </span>
        </div>

        <div className="overflow-x-auto rounded-dc-sm border border-slate-200/80">
          <table className="w-full text-left text-xs text-slate-600">
            <thead className="border-b border-slate-200/80 bg-slate-50/80 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3.5 font-semibold">Promo Code</th>
                <th className="px-5 py-3.5 font-semibold">Discount</th>
                <th className="px-5 py-3.5 font-semibold">Status</th>
                <th className="px-5 py-3.5 font-semibold">Usage Stats</th>
                <th className="px-5 py-3.5 font-semibold">Expires At</th>
                <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {promos.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-xs text-slate-400">
                    No coupons created yet.
                  </td>
                </tr>
              ) : (
                promos.map((p) => (
                  <PromoRow key={p.id} promo={p} onToggle={toggle.mutate} onDelete={remove.mutate} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function statusOf(p) {
  if (p.is_disabled) return { label: 'Disabled', cls: 'bg-slate-100 text-slate-600 border border-slate-200' };
  if (p.expires_at && new Date(p.expires_at) < new Date()) return { label: 'Expired', cls: 'bg-amber-50 text-amber-700 border border-amber-200' };
  if (p.max_uses != null && p.used_count >= p.max_uses) return { label: 'Used up', cls: 'bg-amber-50 text-amber-700 border border-amber-200' };
  return { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200/80' };
}

function PromoRow({ promo: p, onToggle, onDelete }) {
  const status = statusOf(p);
  const discount = p.discount_type === 'percentage' ? `${p.discount_value}%` : `$${p.discount_value}`;
  return (
    <tr className="hover:bg-slate-50/50 transition-colors">
      <td className="px-5 py-3.5 font-mono font-bold text-slate-900">
        <span className="inline-block rounded-dc-sm bg-slate-100 border border-slate-200/80 px-2.5 py-1 text-xs text-slate-900">
          {p.code}
        </span>
      </td>
      <td className="px-5 py-3.5">
        <span className="inline-flex items-center rounded-full bg-brand-50 border border-brand-200 px-2.5 py-0.5 text-[10px] font-bold text-brand-700">
          {discount} OFF
        </span>
      </td>
      <td className="px-5 py-3.5">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold ${status.cls}`}>
          {status.label}
        </span>
      </td>
      <td className="px-5 py-3.5 text-slate-600 font-medium">
        {p.used_count}{p.max_uses != null ? ` / ${p.max_uses}` : ''} used
      </td>
      <td className="px-5 py-3.5 text-slate-500 font-medium">
        {p.expires_at ? new Date(p.expires_at).toLocaleDateString() : '—'}
      </td>
      <td className="px-5 py-3.5">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => onToggle({ id: p.id, isDisabled: !p.is_disabled })}
            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-bold text-slate-700 transition-all hover:bg-slate-900 hover:text-white cursor-pointer active:scale-95"
          >
            {p.is_disabled ? 'Enable' : 'Disable'}
          </button>
          <button
            onClick={() => onDelete(p.id)}
            title="Delete coupon"
            className="inline-flex items-center justify-center rounded-full p-2 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600 cursor-pointer"
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function CustomSelect({ options, value, onChange, disabled, placeholder }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const items = options.map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt));
  const selected = items.find((it) => it.value === value);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={`w-full h-10 flex items-center justify-between rounded-dc-sm border border-slate-300/90 px-3.5 text-xs font-medium outline-none transition-all cursor-pointer shadow-2xs ${
          disabled ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : 'bg-white text-slate-800 hover:border-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10'
        }`}
      >
        <span className="truncate">{selected?.label || placeholder || 'Select...'}</span>
        <svg className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180 text-brand-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>

      {open && !disabled && (
        <div className="absolute z-50 mt-1.5 w-full min-w-[200px] rounded-dc border border-slate-200/80 bg-white py-1.5 shadow-xl max-h-52 overflow-y-auto custom-scrollbar animate-dc-rise">
          {items.map((it) => (
            <button
              key={it.value}
              type="button"
              onClick={() => {
                onChange(it.value);
                setOpen(false);
              }}
              className={`w-full px-3.5 py-2 text-left text-xs transition-colors hover:bg-brand-600 hover:text-white cursor-pointer ${value === it.value ? 'bg-brand-50 font-bold text-brand-700' : 'text-slate-700 font-medium'}`}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomDatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const initialDate = value ? new Date(`${value}T00:00:00`) : new Date();
  const [currentMonth, setCurrentMonth] = useState(initialDate);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const blanks = Array.from({ length: firstDay }, (_, i) => i);

  const handleSelect = (day) => {
    const yyyy = currentMonth.getFullYear();
    const mm = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    onChange(`${yyyy}-${mm}-${dd}`);
    setOpen(false);
  };

  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full h-10 flex items-center justify-between rounded-dc-sm border border-slate-300/90 px-3.5 text-xs font-medium outline-none transition-all cursor-pointer shadow-2xs ${
          open ? 'bg-white border-brand-500 ring-2 ring-brand-500/10' : 'bg-white hover:border-slate-400'
        } text-slate-800`}
      >
        <span>{value ? new Date(`${value}T00:00:00`).toLocaleDateString() : 'Select expiration date...'}</span>
        <FiCalendar className={`w-4 h-4 transition-colors ${open ? 'text-brand-600' : 'text-slate-400'}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 w-72 rounded-dc border border-slate-200/80 bg-white p-4 shadow-xl animate-dc-rise">
          <div className="flex items-center justify-between mb-3">
            <button onClick={prevMonth} type="button" className="p-1 rounded-full hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer">
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <div className="font-display text-xs font-bold text-slate-900">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            <button onClick={nextMonth} type="button" className="p-1 rounded-full hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer">
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-7 gap-1 mb-1 text-center">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <div key={d} className="text-[9.5px] font-extrabold uppercase text-slate-400">{d}</div>
            ))}
          </div>
          
          <div className="grid grid-cols-7 gap-1 text-center">
            {blanks.map(b => <div key={`blank-${b}`} className="h-7"></div>)}
            {days.map(d => {
              const isSelected = value && new Date(`${value}T00:00:00`).getDate() === d && new Date(`${value}T00:00:00`).getMonth() === currentMonth.getMonth() && new Date(`${value}T00:00:00`).getFullYear() === currentMonth.getFullYear();
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => handleSelect(d)}
                  className={`h-7 w-7 mx-auto rounded-full text-xs font-bold transition-all flex items-center justify-center cursor-pointer ${
                    isSelected ? 'bg-slate-900 text-white shadow-xs' : 'text-slate-700 hover:bg-brand-50 hover:text-brand-700'
                  }`}
                >
                  {d}
                </button>
              )
            })}
          </div>
          
          {value && (
            <div className="mt-3 pt-2 border-t border-slate-100 text-center">
               <button type="button" onClick={() => { onChange(''); setOpen(false); }} className="text-[11px] font-bold text-slate-500 hover:text-rose-600 transition-colors cursor-pointer">
                 Clear Selection
               </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
